import { Logo } from '@/components/logo'
import { OnboardingLayout } from '@/components/onboarding-layout'
import { SignInForm } from '@tempots/beatui/auth'
import { attr, Fragment, html, Use, on } from '@tempots/dom'
import { Anchor, HTMLTitle, Location } from '@tempots/ui'
import { ApiError, Auth } from '@/services/auth-service'
import { RequireAnon } from '@/components/require-anon'
import { setPendingVerificationEmail } from '@/stores/auth-store'
import { Track } from '@/services/track'

export function SignInPage() {
  return RequireAnon({}, () =>
    OnboardingLayout(
      {},
      HTMLTitle('Sign In - ResumeHeaders'),
      html.div(
        attr.class('w-full md:max-w-md'),
        html.div(
          attr.class('flex justify-center mb-6 md:mb-8'),
          Anchor({ href: '/' }, Logo({ size: 'main' }))
        ),
        html.h2(
          attr.class('text-lg md:text-xl font-bold text-center mb-3 md:mb-4'),
          'Welcome to ResumeHeaders.com!'
        ),
        html.p(
          attr.class(
            'text-center mb-6 md:mb-8 text-sm md:text-base text-gray-600'
          ),
          'Start your free 14-day trial — no credit card required.'
        ),
        Use(Location, location =>
          Fragment(
            SignInForm({
              showRememberMe: true,
              onSignIn: async data => {
                Track.click({
                  section: 'main',
                  element_type: 'button',
                  click_action: 'submit',
                  element_id: 'signin',
                })
                try {
                  const { user, message } = await Auth.signin({
                    email: data.email,
                    password: data.password,
                  })
                  Track.auth({
                    action: 'signin',
                    user_id: user.id,
                    message,
                  })
                  location.navigate('/start')
                  return null
                } catch (error) {
                  if (error instanceof ApiError) {
                    if (error.code === 'EMAIL_NOT_VERIFIED') {
                      setPendingVerificationEmail(data.email)
                      location.navigate(
                        `/verify-email?email=${encodeURIComponent(data.email)}`
                      )
                      return (
                        error.message ||
                        'Please verify your email before signing in.'
                      )
                    }
                    Track.apiError({
                      error_code: error.code,
                      error_message: error.message,
                    })
                    return (
                      error.message ??
                      'Unable to complete sign in. Please try again.'
                    )
                  }

                  Track.clientError({
                    error_code: 'SIGNIN_FAILED',
                    error_message:
                      error instanceof Error ? error.message : String(error),
                  })
                  return 'An unexpected error occurred. Please try again.'
                }
              },
            }),
            html.div(
              attr.class(
                'mt-8 flex flex-col gap-2 text-sm text-center text-gray-600'
              ),
              html.span(
                "Don't have an account? ",
                html.button(
                  attr.type('button'),
                  attr.class('text-teal-600 hover:underline font-medium'),
                  on.click(() => {
                    Track.click({
                      section: 'main',
                      element_type: 'button',
                      click_action: 'navigate',
                      element_id: 'signup',
                    })
                    location.navigate('/signup')
                  }),
                  'Create one'
                )
              ),
              html.span(
                'Forgot your password? ',
                html.button(
                  attr.type('button'),
                  attr.class('text-teal-600 hover:underline font-medium'),
                  on.click(() => {
                    Track.click({
                      section: 'main',
                      element_type: 'button',
                      click_action: 'navigate',
                      element_id: 'forgot_password',
                    })
                    location.navigate('/forgot-password')
                  }),
                  'Reset it'
                )
              )
            )
          )
        )
      )
    )
  )
}
